import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { EmptyState } from '@/components/patterns/empty-state'
import { SelectableSurface } from '@/components/patterns/selectable-surface'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useAbility, useIsReadOnly } from '@/lib/casl'
import { useFieldContext, useFormContext } from '@/lib/form-contexts'
import {
  designCollectionsFilters,
  useCollections,
} from '@/lib/services/collections/collections.query'
import { useAutomaticNavigation } from '@/lib/services/designs/designs.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import _get from 'lodash/get'
import { Eye, EyeOff, ListTree, Pencil, Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import AutomaticNavigationMenuList from './AutomaticNavigationMenuList'
import {
  applyCollectionVisibilityUpdates,
  getCollectionStorefrontVisibilityIssue,
  getStorefrontProductCount,
  updateCollectionVisibilityDraft,
} from './designForm.utils'
import NavigationMenuItemDialog from './NavigationMenuItemDialog'
import NavigationMenuList from './NavigationMenuList'
import NavigationVisibilityControl from './NavigationVisibilityControl'
import {
  getNavigationItem,
  getNavigationMoveConsequences,
  pruneUnusedCollectionVisibilityUpdates,
  NAVIGATION_ITEMS_MODE,
  NAVIGATION_ITEM_TYPE,
  mergeAutomaticNavigationItems,
  navigationItemsForCustomMode,
  navigationItemLabel,
  navigationPlacementLabel,
  normalizeNavigationTree,
  removeNavigationItemAndEmptyGroups,
  upsertNavigationItem,
} from './navigationMenuEditor.utils'

function ModeOption({ mode, currentMode, title, description, icon: Icon, onSelect, disabled }) {
  const isSelected = currentMode === mode

  return (
    <SelectableSurface
      type="button"
      selected={isSelected}
      radius="surface"
      className={cn(
        'flex min-w-0 flex-1 items-start gap-3 text-left',
        disabled && 'bg-muted text-muted-foreground'
      )}
      disabled={disabled}
      onClick={() => onSelect(mode)}
    >
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-lg',
          isSelected ? 'bg-action text-action-foreground' : 'bg-muted text-muted-foreground'
        )}
      >
        <Icon className="size-4" />
      </span>
      <span className="min-w-0">
        <span className="block text-sm font-semibold">{title}</span>
        <span className="mt-1 block text-xs leading-5 text-muted-foreground">{description}</span>
      </span>
    </SelectableSurface>
  )
}

function navigationItemIsHidden(item, collectionsById) {
  if (item.is_enabled === false) return true

  return (
    item.type === NAVIGATION_ITEM_TYPE.COLLECTION &&
    getCollectionStorefrontVisibilityIssue(
      collectionsById.get(Number(item.collection_id ?? item.collection?.id))
    ) !== null
  )
}

function collectHiddenNavigationItemIds(items, collectionsById, hiddenIds = new Set()) {
  for (const item of items) {
    if (navigationItemIsHidden(item, collectionsById)) {
      hiddenIds.add(String(item.id))
    }
    collectHiddenNavigationItemIds(item.children ?? [], collectionsById, hiddenIds)
  }

  return hiddenIds
}

function collectVisibleNavigationItems(
  items,
  collectionsById,
  initiallyHiddenItemIds,
  showHiddenItems
) {
  const visibleItemIds = new Set()
  let hiddenItemCount = 0

  const addSubtree = (item) => {
    visibleItemIds.add(String(item.id))
    for (const child of item.children ?? []) addSubtree(child)
  }

  const countSubtree = (item) =>
    1 + (item.children ?? []).reduce((count, child) => count + countSubtree(child), 0)

  const visit = (item) => {
    const shouldCollapse =
      initiallyHiddenItemIds.has(String(item.id)) && navigationItemIsHidden(item, collectionsById)

    if (shouldCollapse) {
      hiddenItemCount += countSubtree(item)
      if (showHiddenItems) addSubtree(item)
      return
    }

    visibleItemIds.add(String(item.id))
    for (const child of item.children ?? []) visit(child)
  }

  for (const item of items) visit(item)

  return { hiddenItemCount, visibleItemIds }
}

function HiddenItemsDisclosure({ count, expanded, onClick }) {
  if (!count) return null

  const Icon = expanded ? EyeOff : Eye

  return (
    <Button
      type="button"
      variant="ghostMuted"
      size="xs"
      className="-ml-2"
      aria-expanded={expanded}
      onClick={onClick}
    >
      <Icon />
      {expanded ? 'Hide hidden items' : `Show ${count} hidden ${count === 1 ? 'item' : 'items'}`}
    </Button>
  )
}

export default function NavigationMenuEditor({ modeFieldName, showModeControls = true }) {
  const field = useFieldContext()
  const form = useFormContext()
  const ability = useAbility()
  const { isReadOnly } = useIsReadOnly()
  const { data: collections = [] } = useCollections({ filters: designCollectionsFilters })
  const mode = useStore(form.store, (state) => _get(state.values, modeFieldName)) ?? 'automatic'
  const designId = useStore(form.store, (state) => state.values.id)
  const collectionVisibilityUpdates = useStore(
    form.store,
    (state) => state.values.collection_visibility_updates
  )
  const automaticNavigation = useAutomaticNavigation({
    designId,
    enabled: mode === NAVIGATION_ITEMS_MODE.AUTOMATIC,
  })
  const projectedCollections = applyCollectionVisibilityUpdates(
    collections,
    collectionVisibilityUpdates
  )
  const collectionsById = new Map(
    projectedCollections.map((collection) => [Number(collection.id), collection])
  )
  const savedCollectionsById = new Map(
    collections.map((collection) => [Number(collection.id), collection])
  )
  const canEditCollections = ability.can('edit', 'collections')
  const storedItems = normalizeNavigationTree(field.state.value ?? [])
  const items =
    mode === NAVIGATION_ITEMS_MODE.AUTOMATIC
      ? mergeAutomaticNavigationItems(storedItems, automaticNavigation.data?.items ?? [])
      : storedItems
  const defaultMode = _get(form.options.defaultValues, modeFieldName) ?? 'automatic'
  const defaultStoredItems = normalizeNavigationTree(
    _get(form.options.defaultValues, field.name) ?? []
  )
  const disclosureBaselineItems =
    mode === NAVIGATION_ITEMS_MODE.AUTOMATIC
      ? mergeAutomaticNavigationItems(
          defaultMode === NAVIGATION_ITEMS_MODE.AUTOMATIC ? defaultStoredItems : [],
          automaticNavigation.data?.items ?? []
        )
      : defaultMode === NAVIGATION_ITEMS_MODE.CUSTOM
        ? defaultStoredItems
        : []
  const [editorState, setEditorState] = useState(null)
  const [removeItem, setRemoveItem] = useState(null)
  const [showAutomaticConfirmation, setShowAutomaticConfirmation] = useState(false)
  const [showHiddenItems, setShowHiddenItems] = useState(false)
  const initiallyHiddenItemIds = collectHiddenNavigationItemIds(
    disclosureBaselineItems,
    savedCollectionsById
  )
  const { hiddenItemCount, visibleItemIds } = collectVisibleNavigationItems(
    items,
    collectionsById,
    initiallyHiddenItemIds,
    showHiddenItems
  )
  const loadAutomaticItems = async () => {
    const result = await automaticNavigation.refetch()
    if (result.error) throw result.error

    return normalizeNavigationTree(result.data?.items ?? [])
  }

  const handleModeSelect = async (nextMode) => {
    if (nextMode === mode || isReadOnly) return

    if (nextMode === NAVIGATION_ITEMS_MODE.AUTOMATIC) {
      setShowAutomaticConfirmation(true)
      return
    }

    try {
      const completeItems = mergeAutomaticNavigationItems(storedItems, await loadAutomaticItems())
      const customSourceItems = completeItems.filter((item) => {
        if (item.type !== NAVIGATION_ITEM_TYPE.COLLECTION) return true

        return (
          getCollectionStorefrontVisibilityIssue(
            collectionsById.get(Number(item.collection_id ?? item.collection?.id))
          ) === null
        )
      })

      form.setFieldValue(modeFieldName, NAVIGATION_ITEMS_MODE.CUSTOM, { touch: true })
      setShowHiddenItems(false)
      field.setValue(
        navigationItemsForCustomMode(customSourceItems, mode, {
          preserveAutomaticOrder: true,
        })
      )
    } catch (error) {
      toast.error('Unable to load automatic navigation', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleAutomaticConfirm = async () => {
    try {
      const automaticItems = await loadAutomaticItems()
      form.setFieldValue(modeFieldName, NAVIGATION_ITEMS_MODE.AUTOMATIC, { touch: true })
      setShowHiddenItems(false)
      field.setValue(automaticItems)
    } catch (error) {
      toast.error('Unable to load automatic navigation', {
        description: getErrorMessage(error),
      })
      throw error
    }
  }

  const openEditor = (itemId = null, parentId = null) => {
    const location = itemId === null ? null : getNavigationItem(items, itemId)
    setEditorState({ item: location?.item ?? null, parentId })
  }

  const setItemsAndReconcileVisibility = (nextItems) => {
    field.setValue(nextItems)

    const currentVisibilityUpdates = Array.isArray(collectionVisibilityUpdates)
      ? collectionVisibilityUpdates
      : []
    const nextVisibilityUpdates = pruneUnusedCollectionVisibilityUpdates(
      nextItems,
      currentVisibilityUpdates
    )

    if (nextVisibilityUpdates.length !== currentVisibilityUpdates.length) {
      form.setFieldValue('collection_visibility_updates', nextVisibilityUpdates, { touch: true })
    }
  }

  const handleItemSave = ({ item, parentId, previousId, replacedItemId, append }) => {
    const nextItems = upsertNavigationItem(items, item, {
      previousId: previousId ?? editorState?.item?.id,
      replacedItemId,
      parentId,
      preservePosition: !append,
      insertAtBeginning: previousId == null && editorState?.item == null,
    })

    setItemsAndReconcileVisibility(nextItems)
  }

  const handleRemoveConfirm = () => {
    setItemsAndReconcileVisibility(removeNavigationItemAndEmptyGroups(items, removeItem.id))
    setRemoveItem(null)
  }

  const handleItemVisibilityChange = (itemId, isEnabled) => {
    const location = getNavigationItem(items, itemId)
    if (!location || isReadOnly) return

    field.setValue(
      upsertNavigationItem(
        items,
        { ...location.item, is_enabled: isEnabled },
        { previousId: itemId, parentId: location.parentId }
      )
    )
  }

  const handleCustomItemVisibilityChange = (item, collection, isEnabled) => {
    const savedCollection = collections.find(
      (candidate) => Number(candidate.id) === Number(item.collection_id)
    )
    let nextCollectionVisibility = null

    if (item.type === NAVIGATION_ITEM_TYPE.COLLECTION) {
      if (isEnabled && collection?.is_published === false) {
        nextCollectionVisibility = true
      } else if (
        !isEnabled &&
        savedCollection?.is_published === false &&
        collection?.is_published !== false
      ) {
        nextCollectionVisibility = false
      }
    }

    if (typeof nextCollectionVisibility === 'boolean') {
      form.setFieldValue(
        'collection_visibility_updates',
        updateCollectionVisibilityDraft(
          collections,
          collectionVisibilityUpdates,
          collection.id,
          nextCollectionVisibility
        ),
        { touch: true }
      )
    }

    handleItemVisibilityChange(item.id, isEnabled)
  }

  const renderCustomItemEnd = ({ item, collection }) => {
    const itemLabel = navigationItemLabel(item, collectionsById)

    if (item.type !== NAVIGATION_ITEM_TYPE.COLLECTION) {
      return (
        <NavigationVisibilityControl
          checked={item.is_enabled !== false}
          disabled={isReadOnly}
          itemLabel={itemLabel}
          onCheckedChange={(checked) => handleItemVisibilityChange(item.id, checked)}
        />
      )
    }

    const hasProducts = Number(getStorefrontProductCount(collection) ?? 0) > 0
    const isPublished = collection?.is_published !== false
    const disabled = isReadOnly || !hasProducts || (!isPublished && !canEditCollections)
    const switchControl = (
      <NavigationVisibilityControl
        checked={hasProducts && isPublished && item.is_enabled !== false}
        disabled={disabled}
        itemLabel={itemLabel}
        onCheckedChange={(checked) => handleCustomItemVisibilityChange(item, collection, checked)}
      />
    )

    if (!hasProducts || (!isPublished && !canEditCollections)) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>{switchControl}</TooltipTrigger>
          <TooltipContent variant="warning" className="max-w-64">
            {!hasProducts
              ? 'Add a visible product before this collection can appear.'
              : 'You don’t have permission to change collection visibility.'}
          </TooltipContent>
        </Tooltip>
      )
    }

    return switchControl
  }

  const removeItemEmptiedGroupLabel = removeItem
    ? getNavigationMoveConsequences(items, {
        previousId: removeItem.id,
        parentId: null,
      })
        .emptiedGroupIds.map((groupId) => navigationPlacementLabel(items, groupId))
        .at(0)
    : null

  return (
    <div className="space-y-5">
      {showModeControls ? (
        <div className="space-y-2">
          <Typography variant="label">How should this menu be managed?</Typography>
          <div className="flex flex-col gap-3 @md:flex-row">
            <ModeOption
              mode={NAVIGATION_ITEMS_MODE.AUTOMATIC}
              currentMode={mode}
              title="Automatic"
              description="We’ll keep available collections in the menu. You choose their order."
              icon={ListTree}
              onSelect={handleModeSelect}
              disabled={isReadOnly || automaticNavigation.isFetching}
            />
            <ModeOption
              mode={NAVIGATION_ITEMS_MODE.CUSTOM}
              currentMode={mode}
              title="Custom"
              description="Choose the links, dropdowns, and order yourself."
              icon={Pencil}
              onSelect={handleModeSelect}
              disabled={isReadOnly || automaticNavigation.isFetching}
            />
          </div>
        </div>
      ) : null}

      {mode === NAVIGATION_ITEMS_MODE.AUTOMATIC ? (
        items.length ? (
          <div className="space-y-3">
            <div>
              <Typography variant="label">Menu order</Typography>
              <Typography variant="caption" className="block">
                Drag to reorder. You can also show or hide each item.
              </Typography>
            </div>
            <HiddenItemsDisclosure
              count={hiddenItemCount}
              expanded={showHiddenItems}
              onClick={() => setShowHiddenItems((current) => !current)}
            />
            <AutomaticNavigationMenuList
              items={items}
              collections={collections}
              onChange={field.setValue}
              visibleItemIds={visibleItemIds}
            />
          </div>
        ) : null
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Typography variant="label">Menu items</Typography>
              <Typography variant="caption" className="block">
                Drag to reorder. Hide items without removing them, or edit one to move it into or
                out of a dropdown.
              </Typography>
            </div>
            {!isReadOnly ? (
              <Button size="sm" onClick={() => openEditor()}>
                <Plus /> Add menu item
              </Button>
            ) : null}
          </div>

          <HiddenItemsDisclosure
            count={hiddenItemCount}
            expanded={showHiddenItems}
            onClick={() => setShowHiddenItems((current) => !current)}
          />

          {items.length ? (
            <NavigationMenuList
              items={items}
              collectionsById={collectionsById}
              onEdit={(itemId, parentId) => openEditor(itemId, parentId)}
              onRemove={setRemoveItem}
              onAddChild={(parentId) => openEditor(null, parentId)}
              onReorder={field.setValue}
              getItemClassName={({ item }) =>
                item.is_enabled === false ||
                (item.type === NAVIGATION_ITEM_TYPE.COLLECTION &&
                  getCollectionStorefrontVisibilityIssue(
                    collectionsById.get(Number(item.collection_id))
                  ) !== null)
                  ? 'bg-muted/30 text-muted-foreground'
                  : null
              }
              renderItemEnd={renderCustomItemEnd}
              visibleItemIds={visibleItemIds}
            />
          ) : (
            <EmptyState
              size="compact"
              title="No menu items yet"
              description="Add a collection, custom link, or dropdown menu."
            />
          )}
        </div>
      )}

      {editorState ? (
        <NavigationMenuItemDialog
          key={`${editorState.item?.id ?? 'new'}-${editorState.parentId ?? 'top'}`}
          item={editorState.item}
          parentId={editorState.parentId}
          items={items}
          collections={projectedCollections}
          onSave={handleItemSave}
          onClose={() => setEditorState(null)}
        />
      ) : null}

      <ConfirmationDialog
        open={showAutomaticConfirmation}
        onOpenChange={setShowAutomaticConfirmation}
        trigger={null}
        title="Use automatic navigation?"
        description="When you save, your custom links and dropdowns will be replaced with your available collections."
        confirmText="Use automatic"
        pendingText="Loading menu..."
        isPending={automaticNavigation.isFetching}
        closeOnError={false}
        onConfirm={handleAutomaticConfirm}
      />

      <ConfirmationDialog
        open={removeItem !== null}
        onOpenChange={(open) => !open && setRemoveItem(null)}
        trigger={null}
        variant="destructive"
        title="Remove menu item?"
        description={
          removeItem?.type === NAVIGATION_ITEM_TYPE.MENU ? (
            'This also removes every link inside this dropdown.'
          ) : removeItemEmptiedGroupLabel ? (
            <>
              Remove {navigationItemLabel(removeItem, collectionsById)} from the menu?{' '}
              <strong className="font-semibold">{removeItemEmptiedGroupLabel}</strong> will also be
              removed because it would be empty.
            </>
          ) : (
            `Remove ${navigationItemLabel(removeItem, collectionsById)} from the menu?`
          )
        }
        confirmText="Remove"
        onConfirm={handleRemoveConfirm}
      />
    </div>
  )
}
